import { FiberManualRecord } from '@mui/icons-material'
import { Box, keyframes, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { FlexBox, GapBox } from 'components/Box'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { ReportStatusChip } from 'features/yieldbirdWrapperAgent/components/ReportStatusChip'

import type { ReportStatusResponse } from 'features/yieldbirdWrapperAgent/api/types'
import type { TrackedReport } from 'features/yieldbirdWrapperAgent/types'

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.2; }
`

type Props = {
  isStalled: boolean
  isTracking: boolean
  report: TrackedReport
  reportStatus?: ReportStatusResponse
}

export const TrackedReportSection = ({
  isStalled,
  isTracking,
  report,
  reportStatus,
}: Props): React.JSX.Element => {
  const { t } = useTranslation('features/yieldbirdWrapperAgent')

  return (
    <Paper>
      <SectionHeader
        title={isTracking ? t('tracking.title.tracking') : t('tracking.title.finished')}
        titleEndAdornment={
          isTracking ? (
            <FlexBox axis='x'>
              <FiberManualRecord
                color='success'
                data-cy='live-icon'
                fontSize='small'
                sx={{ animation: `${pulse} 1.5s ease-in-out infinite` }}
              />

              <Typography>{t('tracking.live')}</Typography>
            </FlexBox>
          ) : undefined
        }
      />

      <GapBox />

      <Box
        sx={{
          columnGap: 2,
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          rowGap: 2,
        }}
      >
        <Typography>{t('tracking.url')}:</Typography>
        <Typography>{report.url}</Typography>

        {reportStatus && (
          <>
            <Typography>{t('tracking.status')}:</Typography>
            <Box>
              <ReportStatusChip status={reportStatus.status} />
            </Box>

            <Typography>{t('tracking.description')}:</Typography>
            {isStalled ? (
              <Typography color='warning'>{t('tracking.stalled')}</Typography>
            ) : (
              <Typography>{t(`status.${reportStatus.status}`)}</Typography>
            )}
          </>
        )}

        {reportStatus?.error && (
          <>
            <Typography>{t('tracking.error')}:</Typography>
            <Typography color='error'>{reportStatus.error}</Typography>
          </>
        )}
      </Box>
    </Paper>
  )
}
